import { useEffect, useMemo, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { sortBy } from 'lodash-es'
import AddIcon from '@mui/icons-material/Add'
import CancelIcon from '@mui/icons-material/Cancel'

import { ISelectOption } from '$/types'
import { useAppDispatch } from 'app/hooks'
import {
  useGetQRAScenarioAttributesByCodeQuery,
  useGetQRAScenarioAttributesByThreatIdQuery
} from 'app/services/api/qra/assessmentScenarios'
import { SelectDropdownWrapper } from 'common/components/SelectDropdownWrapper'
import { removeDuplicateAttributesOptions } from 'common/utils/attributeUtils'

import AttributeRule from 'features/RiskManagement/Assessments/Scenarios/AttributeRule'

import { stringRuleBool } from './stringRuleBool'
import { IConditionWithId, IRuleWithId } from './types'
import {
  setStringAttributeCode,
  setStringAttributeOptionsSync
} from './whatIfScenarioSlice'

interface IAttributeField {
  selectedThreat: ISelectOption
  editRule: (rule: IRuleWithId) => void
  ruleValues: IRuleWithId
  removeRule: () => void
  updateConditionsInRule: (id: number, conditions: IConditionWithId[]) => void
  disableDropdownClear?: boolean
}

interface IOption {
  id: string
  value: string
  label: string
  type: string
}

interface IRenderRuleProps {
  selectedAttribute: IOption
  conditions: IConditionWithId[]
  setConditions: React.Dispatch<React.SetStateAction<IConditionWithId[]>>
  removeCondition: (id: number) => void
  addRuleCondition: () => void
  disableDropdownClear?: boolean
}

const RenderRule = ({
  selectedAttribute,
  conditions,
  setConditions,
  removeCondition,
  addRuleCondition,
  disableDropdownClear = false
}: IRenderRuleProps) => {
  if (selectedAttribute != null && selectedAttribute.value !== '') {
    return (
      <div className='my-2 flex'>
        <div className='w-full bg-gray-200 p-3'>
          {conditions.map((condition, index) => {
            return (
              <div key={`${condition.id}-${index}`} className='mb-2 flex'>
                <AttributeRule
                  attributeType={selectedAttribute.type}
                  attributeName={selectedAttribute.label}
                  attributeCode={selectedAttribute.value}
                  selectedCondition={condition}
                  setConditions={setConditions}
                  disableDropdownClear={disableDropdownClear}
                />
                <button
                  type='button'
                  className='ml-2 text-gray-700 focus:outline-none'
                  onClick={() => removeCondition(condition.id)}
                >
                  <CancelIcon />
                </button>
              </div>
            )
          })}
          <button
            type='button'
            className='align-middle font-medium text-lighthouseBlue'
            onClick={addRuleCondition}
          >
            <AddIcon className='mr-2' />
            Add Rule
          </button>
        </div>
      </div>
    )
  } else {
    return null
  }
}

const AttributeField = ({
  selectedThreat,
  removeRule,
  ruleValues,
  editRule,
  updateConditionsInRule,
  disableDropdownClear = false
}: IAttributeField) => {
  const dispatch = useAppDispatch()

  const { currentData: scenarioAttributes } =
    useGetQRAScenarioAttributesByThreatIdQuery(
      (selectedThreat?.value as string) ?? ''
    )

  // ? why was condition id set to index, but conditionId starts at 1
  const [conditions, setConditions] = useState<IConditionWithId[]>(
    ruleValues.conditions.map((condition, index) => ({
      ...condition,
      id: index + 1
    }))
  )

  const [conditionId, setConditionId] = useState<number>(conditions.length + 1)

  const [selectedAttribute, setSelectedAttribute] = useState<IOption>({
    id: ruleValues.attributeId,
    value: ruleValues.attributeCode,
    type: ruleValues.attributeType,
    label: ruleValues.attributeName
  })

  const { currentData: scenarioAttributeOptions } =
    useGetQRAScenarioAttributesByCodeQuery(
      selectedAttribute?.value ?? skipToken
    )

  const attributeOptions = useMemo(() => {
    if (scenarioAttributes != null) {
      const sortedScenarioAttributes = sortBy(scenarioAttributes, [
        (attribute) => attribute.code
      ])

      return sortedScenarioAttributes.map((attribute) => {
        return {
          id: attribute.id,
          label: attribute.name,
          value: attribute.code,
          type: attribute.dataType
        }
      })
    }

    return []
  }, [scenarioAttributes])

  // ! TEMP solution to update rules without an infinite loop
  useEffect(() => {
    updateConditionsInRule(ruleValues.id, conditions)
  }, [conditions, ruleValues.id, updateConditionsInRule])

  useEffect(() => {
    if (scenarioAttributeOptions?.length) {
      if (selectedAttribute.type === 'string') {
        /*
        this code line added is a temporal fix to avoid duplicates in Edit WIS modal rule dropboxes
        Ticket for BackEnd team already created to fix it: LHT-2287
      */
        const filteredAttributeOptions = removeDuplicateAttributesOptions(
          scenarioAttributeOptions
        )

        dispatch(setStringAttributeCode(selectedAttribute.value))
        dispatch(setStringAttributeOptionsSync(filteredAttributeOptions))
      }
    }
  }, [dispatch, scenarioAttributeOptions, selectedAttribute])

  const addRuleCondition = () => {
    const _getCondition = (
      attributeType: string,
      defaultValue = 'Less Than'
    ) => {
      if (attributeType === 'bit') {
        return 'Boolean'
      }

      if (attributeType === 'string') {
        return 'String'
      }

      return defaultValue
    }

    setConditions([
      ...conditions,
      {
        id: conditionId,
        beforeCondition: _getCondition(ruleValues.attributeType),
        beforeValue:
          ruleValues.attributeType === 'bit' ? stringRuleBool.TRUE : '',
        afterCondition: _getCondition(ruleValues.attributeType, 'Replace With'),
        afterValue:
          ruleValues.attributeType === 'bit' ? stringRuleBool.FALSE : ''
      }
    ])
    setConditionId(conditionId + 1)
  }

  const removeCondition = (id: number): void => {
    const filteredConditions = conditions.filter(
      (condition) => condition.id !== id
    )

    setConditions(filteredConditions)

    editRule({
      ...ruleValues,
      conditions: filteredConditions
    })
  }

  const updateSelection = (selection: IOption): void => {
    setSelectedAttribute(selection)

    editRule({
      ...ruleValues,
      attributeId: selection.id,
      attributeCode: selection.value,
      attributeName: selection.label,
      attributeType: selection.type
    })
    setConditions([])
  }

  return (
    <div>
      <div className='flex justify-between'>
        <SelectDropdownWrapper
          className='my-1 h-10 w-72'
          disableClear={disableDropdownClear}
          options={sortBy(attributeOptions, [
            (i) => i.label.toLowerCase(),
            (i) => i.label
          ])}
          value={selectedAttribute.value || undefined}
          onChange={(_, selection) => {
            if (!Array.isArray(selection)) {
              updateSelection(selection as IOption)
            }
          }}
          placeholder='Select attributes'
          onClear={() => {
            setSelectedAttribute({
              id: '',
              type: '',
              label: '',
              value: ''
            })
            removeRule()
          }}
        />
        {ruleValues.id !== 0 && (
          <button
            type='button'
            className='mr-6 font-bold text-gray-300 focus:outline-none'
            onClick={removeRule}
          >
            REMOVE
          </button>
        )}
      </div>
      <RenderRule
        conditions={conditions}
        selectedAttribute={selectedAttribute}
        setConditions={setConditions}
        addRuleCondition={addRuleCondition}
        removeCondition={removeCondition}
        disableDropdownClear={disableDropdownClear}
      />
    </div>
  )
}

export default AttributeField
